import React, { useEffect, useState } from "react";
import { Card, Heading, Box, Text, Image, Grid, Link } from "theme-ui";
import { AddressZero } from "@ethersproject/constants";
import { Decimal, Percent, LiquityStoreState } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";

import { useLiquity } from "../hooks/LiquityContext";
import { Statistic } from "./Statistic";
import * as l from "../lexicon";

// codeninja
import { TokenPrice } from "./TokenPrice";
import { Icon } from "./Icon";
import INCicon from "../assets/icons/INC.png";
import PRINTicon from "../assets/icons/PRINTtoken.png";
import INCDicon from "../assets/icons/INCDtoken.png";
import { getPrintPrice } from "../utils/tokensPrice";
import { BlockPolledLiquityStoreState } from "@liquity/lib-ethers";

const selectBalances = ({
  accountBalance,
  lusdBalance,
  lqtyBalance,
  sacrificeDetails,
}: LiquityStoreState) => ({
  accountBalance,
  lusdBalance,
  lqtyBalance,
  sacrificeDetails,
});

const Balances: React.FC = () => {
  const {
    accountBalance,
    lusdBalance,
    lqtyBalance,
    sacrificeDetails,
  } = useLiquitySelector(selectBalances);

  return (
    <Box sx={{ mb: 3 }}>
      <Heading>My Account Balances</Heading>
      <Statistic lexicon={l.ETH}>{accountBalance.shorten()}</Statistic>
      <Statistic lexicon={l.LUSD}>{lusdBalance.shorten()}</Statistic>
      <Statistic lexicon={l.LQTY}>{lqtyBalance.shorten()}</Statistic>
      {/* <Statistic lexicon={l.ALLOWANCE}>{plsxTokenAllowance.prettify()}</Statistic> */}
    </Box>
  );
};

// const GitHubCommit: React.FC<{ children?: string }> = ({ children }) =>
//   children?.match(/[0-9a-f]{40}/) ? (
//     <Link href={`https://github.com/liquity/dev/commit/${children}`}>{children.substr(0, 7)}</Link>
//   ) : (
//     <>unknown</>
//   );

type SystemStatsProps = {
  variant?: string;
  showBalances?: boolean;
  onClose?: () => void;
};

const select = ({
  numberOfTroves,
  trove,
  price,
  total,
  lusdInStabilityPool,
  borrowingRate,
  redemptionRate,
  totalStakedLQTY,
  frontend,
  plsxTokenAllowance, //Shinto: PlsxTokenAllowance
  sacrificeDetails,
}: LiquityStoreState) => ({
  numberOfTroves,
  trove,
  price,
  total,
  lusdInStabilityPool,
  borrowingRate,
  redemptionRate,
  totalStakedLQTY,
  kickbackRate: frontend.status === "registered" ? frontend.kickbackRate : null,
  plsxTokenAllowance, //Shinto: PlsxTokenAllowance
  sacrificeDetails,
});

export const SystemStats: React.FC<SystemStatsProps> = ({
  variant = "info",
  showBalances,
  onClose,
}) => {
  const {
    liquity: {
      connection: {
        // codeninja
        addresses: {
          plxToken: plxToken,
          lqtyToken: earnToken,
          lusdToken: pxdcToken,
        },
        bootstrapPeriod,
        deploymentDate,
        version: frontendTag,
      }, //contractsVersion removed
    },
  } = useLiquity();
  const SECONDS_IN_ONE_DAY = 24 * 60 * 60;
  const selectBlockTimestamp = ({ blockTimestamp }: BlockPolledLiquityStoreState) => blockTimestamp;
  
  const blockTimestamp = useLiquitySelector(selectBlockTimestamp);

  const bootstrapPeriodDays = Math.round(bootstrapPeriod / SECONDS_IN_ONE_DAY);
  const deploymentTime = deploymentDate.getTime() / 1000;
  const bootstrapEndTime = deploymentTime + bootstrapPeriod;
  const bootstrapEndDate = new Date(bootstrapEndTime * 1000);
  const redemptionDisabled = blockTimestamp < bootstrapEndTime;

  // codeninja
  const testnet =
    "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/";
  // const pulsexapp = "https://pulsex.mypinata.cloud/ipfs/bafybeidea3ibq4lu5t6vk6ihp4iuznjb3ltsdm5y2shv4atxgyd3d33aim/#/";
  const pulsexapp =
    "https://app.piteas.io/#/swap?inputCurrency=PLS&outputCurrency=";

  // add token to wallet
  const addTokenToWallet = (
    tokenSymbol: string,
    tokenAddress: string,
    tokenImage: string
  ) => async () => {
    if (window.ethereum) {
      try {
        const { ethereum } = window as any;
        let url = "";

        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
          url = `http://localhost:3000/`;
        } else {
          url = `https://dev.d3t4kuh2evv701.amplifyapp.com/`;
        }

        await ethereum.request({
          method: "wallet_watchAsset",
          params: {
            type: "ERC20",
            options: {
              address: tokenAddress,
              symbol: tokenSymbol,
              decimals: 18,
              image: url + tokenImage,
            },
          },
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const {
    numberOfTroves,
    trove,
    price,
    lusdInStabilityPool,
    total,
    borrowingRate,
    totalStakedLQTY,
    kickbackRate,
    plsxTokenAllowance, //Shinto: PlsxTokenAllowance
    sacrificeDetails,
  } = useLiquitySelector(select);

  const lusdInStabilityPoolPct =
    total.debt.nonZero && new Percent(lusdInStabilityPool.div(total.debt));
  const totalCollateralRatioPct = new Percent(total.collateralRatio(price));
  const borrowingFeePct = new Percent(borrowingRate);
  const kickbackRatePct =
    frontendTag === AddressZero ? "100" : kickbackRate?.mul(100).prettify();

  const [tokensPrice, setTokensPrice] = useState({
    PRINT: 0.0,
    INCD: 0.0,
    LOAN: 0.0,
    USDL: 0.0
  });

  useEffect(() => {
    const earnPrices = async () => {
      const response = await getPrintPrice();
      setTokensPrice(response);
      localStorage.setItem("tokensPrice", JSON.stringify(response));
    };

    earnPrices();
  }, []);

  return (
    <Card {...{ variant }}>
      {showBalances && <Balances />}

      <Heading>INC Printer Statistics</Heading>

      {/* codeninja */}
      <Grid sx={{ mt: 30 }} gap={4} columns={[3]}>
        <Box sx={{ textAlign: "center" }}>
          <Image
            sx={{ width: 40, height: 40, cursor: "pointer" }}
            src={INCicon}
            variant="avatar"
            onClick={addTokenToWallet("INC", plxToken, INCicon)}
          />
          <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
            INC
          </Heading>
          <Text sx={{ fontSize: 1, color: "#00afff" }}>
            ${price.prettify(4)}
          </Text>
          <Text sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}>
            <TokenPrice lexicon={l.OPPRINT} />
            <Link href="#" sx={{ mr: 1 }}>
              <Icon name="info-circle" />
            </Link>
            <Link href="#" sx={{ mr: 1 }}>
              <Icon name="satellite" />
            </Link>
          </Text>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Image
            sx={{ width: 40, height: 40, cursor: "pointer" }}
            src={PRINTicon}
            variant="avatar"
            onClick={addTokenToWallet("PRINT", earnToken, PRINTicon)}
          />
          <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
            PRINT
          </Heading>
          <Text sx={{ fontSize: 1, color: "#00afff" }}>
            ${tokensPrice.PRINT}
          </Text>
          <Text sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}>
            <TokenPrice lexicon={l.MPPRINT} />
            <Link href="#" sx={{ mr: 1 }}>
              <Icon name="info-circle" />
            </Link>
            <Link
              target="_blank"
              href={testnet + "address/" + earnToken}
              sx={{ mr: 1 }}
            >
              <Icon name="chart-bar" />
            </Link>
            <Link target="_blank" href={pulsexapp + earnToken}>
              <Icon name="exchange-alt" />
            </Link>
          </Text>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Image
            sx={{ width: 40, height: 40, cursor: "pointer" }}
            src={INCDicon}
            variant="avatar"
            onClick={addTokenToWallet("INCD", pxdcToken, INCDicon)}
          />
          <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
            INCD
          </Heading>
          <Text sx={{ fontSize: 1, color: "#00afff" }}>
            ${tokensPrice.INCD}
          </Text>
          <Text sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}>
            <TokenPrice lexicon={l.MPINCD} />
            <Link href="#" sx={{ mr: 1 }}>
              <Icon name="info-circle" />
            </Link>
            <Link
              target="_blank"
              href={testnet + "address/" + pxdcToken}
              sx={{ mr: 1 }}
            >
              <Icon name="chart-bar" />
            </Link>
            <Link target="_blank" href={pulsexapp + pxdcToken}>
              <Icon name="exchange-alt" />
            </Link>
          </Text>
        </Box>
      </Grid>

      <Heading as="h2" sx={{ mt: 3, fontWeight: "body" }}>
        Protocol
      </Heading>

      <Statistic lexicon={l.BORROW_FEE}>
        {borrowingFeePct.toString(2)}
      </Statistic>

      <Statistic lexicon={l.TVL}>
        {total.collateral.shorten()}{" "}
        <Text sx={{ fontSize: 1 }}>&nbsp;INC</Text>
        <Text sx={{ fontSize: 1 }}>
          &nbsp;(${Decimal.from(total.collateral.mul(price)).shorten()})
        </Text>
      </Statistic>
      <Statistic lexicon={l.TROVES}>
        {Decimal.from(numberOfTroves).prettify(0)}
      </Statistic>
      <Statistic lexicon={l.LUSD_SUPPLY}>{total.debt.shorten()}</Statistic>
      {lusdInStabilityPoolPct && (
        <Statistic lexicon={l.STABILITY_POOL_LUSD}>
          {lusdInStabilityPool.shorten()}
          <Text sx={{ fontSize: 1 }}>
            &nbsp;({lusdInStabilityPoolPct.toString(1)})
          </Text>
        </Statistic>
      )}
      <Statistic lexicon={l.STAKED_LQTY}>{totalStakedLQTY.shorten()}</Statistic>
      <Statistic lexicon={l.TCR}>
        {totalCollateralRatioPct.prettify()}
      </Statistic>
      <Statistic lexicon={l.RECOVERY_MODE}>
        {total.collateralRatioIsBelowCritical(price) ? (
          <Box color="danger">Yes</Box>
        ) : (
          "No"
        )}
      </Statistic>
      {/* Shinto: PlsxTokenAllowance */}
      <Statistic lexicon={l.ALLOWANCE}>
        {plsxTokenAllowance.gt(1e15)
          ? "Infinite"
          : plsxTokenAllowance.shorten()}
      </Statistic>

      <Statistic lexicon={l.LIQUIDIDATION_NORMAL}>
        {!trove.collateral.isZero
          ? trove.debt
              .mul(Decimal.from(110))
              .div(trove.collateral.mul(Decimal.from(100)))
              .toString(7)
          : "0.00"}{" "}
        USD
      </Statistic>

      <Statistic lexicon={l.LIQUIDIDATION_RECOVERY}>
        {!trove.collateral.isZero
          ? trove.debt
              .mul(Decimal.from(150))
              .div(trove.collateral.mul(Decimal.from(100)))
              .toString(7)
          : "0.00"}{" "}
        USD
      </Statistic>

      <Statistic lexicon={l.SYSTEM_OFFSET}>
        { redemptionDisabled
          ? "No"
          : <Link href="/#/redemption" sx={{ mr: 1 }} onClick={onClose}>
            Yes
          </Link>
        }
      </Statistic>

      <Heading as="h2" sx={{ mt: 3, fontWeight: "body" }}>
        {/* Frontend */}
      </Heading>
      {kickbackRatePct && (
        <Statistic lexicon={l.KICKBACK_RATE}>{kickbackRatePct}%</Statistic>
      )}

      <Box sx={{ mt: 3, opacity: 0.66 }}>

      </Box>
    </Card>
  );
};
